import { createSelector, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import api from 'helpers/api.helper';
import { APP_URLS } from 'constants/url.constant';
import history from 'helpers/history.helper';
import ReservationStatus from 'enums/reservation_status.enum';

const name = 'reservation';

const initialState = {
    reservations: [],
    programs: {},
    counselors: {},
    reserved: {},
    registered: { all: {} },
    form: {},
    sessionLength: 50,
    realSessionLength: 50,
    clientRegisteredTimes: {},
    interviewSheet: {},
    firstInterviewSheet: {},
    isFirstTime: null,
};

const reservationSlice = createSlice({
    name,
    initialState: initialState,
    reducers: {
        setReservations(state, action) {
            state.reservations = action.payload;
        },
        setPrograms(state, action) {
            state.programs = action.payload;
        },
        setCounselors(state, action) {
            state.counselors = action.payload;
        },
        setSchedule(state, action) {
            state.counselors = action.payload.counselors;
            state.reserved = action.payload.reserved;
            state.registered = action.payload.registered;
            state.sessionLength = action.payload.sessionLength;
            state.realSessionLength = action.payload.realSessionLength;
            state.clientRegisteredTimes = action.payload.clientRegisteredTimes;
            state.interviewSheet = action.payload.interviewSheet;
            state.firstInterviewSheet = action.payload.firstInterviewSheet;
            state.isFirstTime = action.payload.isFirstTime;
        },
        setForm(state, action) {
            state.form = action.payload;
        },
        setSessionLength(state, action) {
            state.sessionLength = action.payload;
        },
    },
});

export const { setReservations, setPrograms, setCounselors, setSchedule, setForm } = reservationSlice.actions;

export default reservationSlice.reducer;

export const getReservations = () => async dispatch => {
    const data = await api.get(APP_URLS.CLIENT_RESERVATIONS);
    if (data) {
        dispatch(setReservations(data));
    }
};

export const getReservablePrograms = () => async dispatch => {
    const data = await api.get(APP_URLS.CLIENT_PROGRAMS_RESERVABLE);
    if (data) {
        let programs = {};
        for (const program of data) {
            programs[program.id] = program;
        }
        dispatch(setPrograms(programs));
    }
};

export const getReservableCounselors = (companyProgramId, reservationId, clientId) => async dispatch => {
    try {
        const data = await api.get(
            APP_URLS.CLIENT_RESERVATIONS_SCHEDULES,
            {
                company_program_id: companyProgramId,
                reservation_id: reservationId,
                client_id: clientId,
            },
            {},
            true
        );
        if (data) {
            dispatch(setSchedule(data));
        }
    } catch (e) {
        history.goBack();
    }
};

const reservedSelector = state => state.reserved;
const registeredSelector = state => state.registered;
const counselorsSelector = state => state.counselors;
const formSelector = state => state.form;
const sessionLengthSelector = state => state.sessionLength;
const reservationsSelector = state => state.reservations;
const clientRegisteredTimesSelector = state => state.clientRegisteredTimes;

export const timeSelector = createSelector(
    formSelector,
    reservedSelector,
    registeredSelector,
    sessionLengthSelector,
    clientRegisteredTimesSelector,
    (form, reserved, registered, sessionLength, clientRegisteredTimes) => {
        if (!form.date) {
            return {};
        }
        const weekDay = form.date.locale('en').format('dddd').toLowerCase();
        if (!registered.all || !registered.all[weekDay]) {
            return {};
        }
        const dateStr = form.date.format('YYYY-MM-DD');

        if (reserved[dateStr]) {
            let times = {};
            for (const regTime of Object.keys(registered.all[weekDay])) {
                for (const id of registered.all[weekDay][regTime]) {
                    let isFree = false;
                    if (!reserved[dateStr][id]) {
                        // No reserve for this counselor
                        isFree = true;
                    } else {
                        const tmpEndTime = moment(regTime, 'HH:mm')
                            .add(sessionLength - 30, 'minutes')
                            .format('HH:mm');
                        if (
                            reserved[dateStr][id].data.indexOf(regTime) === -1 &&
                            reserved[dateStr][id].data.indexOf(tmpEndTime) === -1
                        ) {
                            isFree = true;
                        }
                    }

                    if (isFree && isFreeTimeOfClient(clientRegisteredTimes, dateStr, regTime, sessionLength)) {
                        if (!times[regTime]) {
                            times[regTime] = [];
                        }
                        times[regTime].push(id);
                    }
                }
            }
            return times;
        } else {
            let times = {};
            const registeredAllWeekDay = registered.all[weekDay];
            Object.keys(registeredAllWeekDay).map(k => {
                if (isFreeTimeOfClient(clientRegisteredTimes, dateStr, k, sessionLength)) {
                    times[k] = registeredAllWeekDay[k];
                }
            });
            return { ...times };
        }
    }
);

export const allTimeSelector = createSelector(
    formSelector,
    reservedSelector,
    registeredSelector,
    (form, reserved, registered) => {
        if (!form.date) {
            return {};
        }
        const weekDay = form.date.locale('en').format('dddd').toLowerCase();
        if (!registered.all || !registered.all[weekDay]) {
            return {};
        }

        let times = {};
        const registeredAllWeekDay = registered.all[weekDay];
        Object.keys(registeredAllWeekDay).map(k => {
            times[k] = registeredAllWeekDay[k];
        });
        return { ...times };
    }
);

const isFreeTimeOfClient = (clientRegisteredTimes, date, regTime, sessionLength) => {
    const tmpEndTime = moment(regTime, 'HH:mm').add(sessionLength, 'minutes').format('HH:mm');
    const registered = clientRegisteredTimes[date];
    if (registered && registered.length > 0) {
        return !registered.find(r => !(r.start_time >= tmpEndTime || r.end_time <= regTime));
    }
    return true;
};

export const counselorSelector = createSelector(
    formSelector,
    counselorsSelector,
    timeSelector,
    (form, counselors, times) => {
        if (!form.date || !form.time || !times) {
            return [];
        }
        const tmp = form.time.split('〜');
        if (!times[tmp[0]]) return [];
        let result = [];
        for (const id of times[tmp[0]]) {
            result.push(counselors[id]);
        }
        return result;
    }
);

export const activeReservationsSelector = createSelector(reservationsSelector, reservations => {
    return reservations.filter(
        r =>
            r.status === ReservationStatus.CREATED.value ||
            r.status === ReservationStatus.FILLED.value ||
            r.status === ReservationStatus.START.value
    );
});

export const pastReservationsSelector = createSelector(reservationsSelector, reservations => {
    return reservations.filter(
        r =>
            r.status === ReservationStatus.END.value ||
            r.status === ReservationStatus.COMPLETED.value ||
            r.status === ReservationStatus.PENDING.value ||
            r.status === ReservationStatus.CANCELLED_TIMEOUT.value ||
            r.status === ReservationStatus.CANCELLED_BOOKING.value
    );
});
